// components/Map/AILocationMarker.jsx

import "./AILocationMarker.styles.scss";

import React, { useEffect, useRef, useState } from "react";
import { Marker, Popup } from "react-map-gl";

import Icon from "../Icon/Icon";

/**
 * A marker for AI-reccomended locations
 * Shows a distinctive style to differentiate from existing location markers
 *
 * @param {Object} location - Location object with lat, lng, score (optional), note (optional)
 * @param {number} index - Index of the location in the list
 * @param {string} label - Optional label for the marker
 * @param {Function} onMarkerClick - Callback when marker is clicked
 * @return {JSX.Element}
 * @constructor
 */
const AILocationMarker = ({ location, index, label, onMarkerClick }) => {
	const markerRef = useRef();
	const popupRef = useRef();
	const [popupOpen, setPopupOpen] = useState(false);

	const handleClickOutside = (event) => {
		if (popupRef.current && !popupRef.current.contains(event.target)) {
			setPopupOpen(false);
			onMarkerClick && onMarkerClick(null);
		}
	};

	useEffect(() => {
		document.addEventListener("click", handleClickOutside, true);
		return () => {
			document.removeEventListener("click", handleClickOutside, true);
		};
	}, []);

	return (
		<>
			{popupOpen && (
				<Popup
					className='ai-location-marker-popup'
					latitude={location.lat}
					longitude={location.lng}
					offset={{ top: [0, 18], bottom: [0, -18] }}
					maxWidth='320px'
					onClose={() => setPopupOpen(false)}
				>
					<div ref={popupRef} className='popup-head'>
						<h6>
							{label || `BC Suggestion ${index + 1}`}
							{location.score && (
								<span className='badge bg-primary ms-2'>{Math.round(location.score)}</span>
							)}
						</h6>
						<div className='popup-body'>
							<p className='mb-1'>
								<strong>Latitude:</strong> {location.lat.toFixed(4)}
							</p>
							<p className='mb-1'>
								<strong>Longitude:</strong> {location.lng.toFixed(4)}
							</p>
							{location.score && (
								<p className='mb-1'>
									<strong>Market Score:</strong> {Math.round(location.score)}
								</p>
							)}
							{location.note && (
								<div className='ai-note mt-2 pt-2 border-top'>
									<p className='mb-0 small'>{location.note}</p>
								</div>
							)}
						</div>
					</div>
				</Popup>
			)}

			<Marker
				latitude={location.lat}
				longitude={location.lng}
				anchor='center'
				onClick={(e) => {
					e.originalEvent.stopPropagation();
					setPopupOpen(!popupOpen);
					onMarkerClick && onMarkerClick(index);
				}}
			>
				<div ref={markerRef} className='ai-location-marker'>
					<div className='ai-marker-pin'>
						<Icon icon='ai' size={"lg"} className=' m-0 border-0' color='primary' theme='primary' />
						<span className='ai-marker-label'>{index}</span>
					</div>
				</div>
			</Marker>
		</>
	);
};

export default AILocationMarker;
